import type { ReactNode } from 'react';
import { useIxcFundingV1SourceQueryServiceSourceGet } from '@interxfi/api-client/generated/hooks/ixc-funding-v1-source-query-service-source-get';
import {
  getReadonlyListItemCurrency,
  getReadonlyListItemExternalId,
  getReadonlyListItemId,
  getReadonlyListItemNode,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemSensitiveString,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import type {
  ReadonlyListItem,
  ReadonlyListItemOneOfRecord,
  ReadonlyListItemRecord,
} from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { defaultValueStackedTypographyProps } from '@interxfi/design-system/components/list/readonly/item/value';
import type {
  IxcFundingV1AccountNumberSource,
  IxcFundingV1AccountNumbersControls,
  IxcFundingV1AccountNumbersControlsSettings,
  IxcFundingV1AchSource,
  IxcFundingV1BatchFundingControls,
  IxcFundingV1BatchFundingControlsLimits,
  IxcFundingV1BatchFundingControlsSettings,
  IxcFundingV1CreatableSourceType,
  IxcFundingV1DomesticWireSource,
  IxcFundingV1ExternallyManaged,
  IxcFundingV1FundingAmountLimits,
  IxcFundingV1FundingControls,
  IxcFundingV1FundingControlsLimits,
  IxcFundingV1FundingControlsLimitsAch,
  IxcFundingV1FundingControlsLimitsSameDayAch,
  IxcFundingV1FundingControlsLimitsWire,
  IxcFundingV1FundingControlsSettings,
  IxcFundingV1FundingControlsSettingsAch,
  IxcFundingV1FundingControlsSettingsSameDayAch,
  IxcFundingV1FundingControlsSettingsWire,
  IxcFundingV1PlaidAccountDetails,
  IxcFundingV1PlaidAccountDetailsAch,
  IxcFundingV1PlaidProcessorTokenSource,
  IxcFundingV1SourceData,
  IxcFundingV1SourceKeys,
  IxcFundingV1SourceSpec,
  IxcFundingV1SourceState,
  IxcFundingV1SourceType,
  IxcFundingV1SourceValidation,
  IxcFundingV1UsBankDetails,
} from '@interxfi/types/generated/api';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { getBooleanFromUndefinedOrNullable } from '@interxfi/utilities/object/access';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import type { ReplaceValuesAndMakeOptional } from '@interxfi/utilities/types/replace';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getRegistrationAccountDetailPath } from '../../registration/account/registration-account-routing';
import { getProgramDetailPath } from '../../userauth/program/program-routing';
import { getReadonlyListItemTenant } from '../../userauth/tenant/userauth-tenant-list-items';
import { maskPlaidProcessorToken } from '../../vendor/plaid/processor-token/plaid-processor-token-helpers';
import { getFundingAccountNumberDetailPath } from '../account-number/funding-account-number-routing';
import { getFundingSourceDetailPath } from './funding-source-routing';
import { FundingSourceStatusPill } from './funding-source-status-pill';
import { getCompactFundingSourceSummaryFromSpec } from './util';

export function getFundingAmountLimitsListItems(data: IxcFundingV1FundingAmountLimits | undefined, prefix = '') {
  return {
    minAmount: getReadonlyListItemCurrency(`${prefix}minAmount`, 'Min Amount', data?.minAmount),
    maxAmount: getReadonlyListItemCurrency(`${prefix}maxAmount`, 'Max Amount', data?.maxAmount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsAchLimitsListItems(data: IxcFundingV1FundingControlsLimitsAch | undefined, prefix = '') {
  return {
    depositPerTransaction: getFundingAmountLimitsListItems(data?.depositPerTransaction, `${prefix}depositPerTransaction.`),
    withdrawalPerTransaction: getFundingAmountLimitsListItems(data?.withdrawalPerTransaction, `${prefix}withdrawalPerTransaction.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsSameDayAchLimitsListItems(data: IxcFundingV1FundingControlsLimitsSameDayAch | undefined, prefix = '') {
  return {
    depositPerTransaction: getFundingAmountLimitsListItems(data?.depositPerTransaction, `${prefix}depositPerTransaction.`),
    withdrawalPerTransaction: getFundingAmountLimitsListItems(data?.withdrawalPerTransaction, `${prefix}withdrawalPerTransaction.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsWireLimitsListItems(data: IxcFundingV1FundingControlsLimitsWire | undefined, prefix = '') {
  return {
    withdrawalPerTransaction: getFundingAmountLimitsListItems(data?.withdrawalPerTransaction, `${prefix}withdrawalPerTransaction.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsLimitsListItems(data: IxcFundingV1FundingControlsLimits | undefined, prefix = '') {
  return {
    maxSourcesPerAccount: getReadonlyListItemNumber(`${prefix}maxSourcesPerAccount`, 'Max Sources per Account', data?.maxSourcesPerAccount),
    ach: getFundingControlsAchLimitsListItems(data?.ach, `${prefix}ach.`),
    achSameDay: getFundingControlsSameDayAchLimitsListItems(data?.sameDayAch, `${prefix}sameDayAch.`),
    wire: getFundingControlsWireLimitsListItems(data?.wire, `${prefix}wire.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsAchSettingsListItems(data: IxcFundingV1FundingControlsSettingsAch | undefined, prefix = '') {
  return {
    canDeposit: getReadonlyListItemYesOrNo(`${prefix}canDeposit`, 'Pull', Boolean(data?.canDeposit)),
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsSameDayAchSettingsListItems(data: IxcFundingV1FundingControlsSettingsSameDayAch | undefined, prefix = '') {
  return {
    canDeposit: getReadonlyListItemYesOrNo(`${prefix}canDeposit`, 'Pull', Boolean(data?.canDeposit)),
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsWireSettingsListItems(data: IxcFundingV1FundingControlsSettingsWire | undefined, prefix = '') {
  return {
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsSettingsListItems(data: IxcFundingV1FundingControlsSettings | undefined, prefix = '') {
  return {
    ach: getFundingControlsAchSettingsListItems(data?.ach, `${prefix}ach.`),
    sameDayAch: getFundingControlsSameDayAchSettingsListItems(data?.sameDayAch, `${prefix}sameDayAch.`),
    wire: getFundingControlsWireSettingsListItems(data?.wire, `${prefix}wire.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingControlsListItems(data: IxcFundingV1FundingControls | undefined, prefix = '') {
  return {
    limits: getFundingControlsLimitsListItems(data?.limits, `${prefix}limits.`),
    settings: getFundingControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingAccountNumbersControlsSettingsListItems(data: IxcFundingV1AccountNumbersControlsSettings | undefined, prefix = '') {
  return {
    canCreate: getReadonlyListItemYesOrNo(`${prefix}canCreate`, 'Can Create', Boolean(data?.canCreate)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingAccountNumbersControlsListItems(data: IxcFundingV1AccountNumbersControls | undefined, prefix = '') {
  return {
    settings: getFundingAccountNumbersControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingBatchControlLimitsListItems(data: IxcFundingV1BatchFundingControlsLimits | undefined, prefix = '') {
  return {
    perBatchMaximums: {
      deposit: getReadonlyListItemCurrency(`${prefix}perBatchMaximums.deposit`, 'Batch Pull Max', data?.perBatchMaximums?.deposit),
      withdrawal: getReadonlyListItemCurrency(`${prefix}perBatchMaximums.withdrawal`, 'Batch Push Max', data?.perBatchMaximums?.withdrawal),
    },
    autoApproveUnder: {
      deposit: getReadonlyListItemCurrency(`${prefix}autoApproveUnder.deposit`, 'Auto-Approve Batch Pulls Under', data?.autoApproveUnder?.deposit),
      withdrawal: getReadonlyListItemCurrency(
        `${prefix}autoApproveUnder.withdrawal`,
        'Auto-Approve Batch Pushes Under',
        data?.autoApproveUnder?.withdrawal,
      ),
    },
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingBatchControlSettingsListItems(data: IxcFundingV1BatchFundingControlsSettings | undefined, prefix = '') {
  return {
    ach: {
      canDeposit: getReadonlyListItemYesOrNo(`${prefix}ach.canDeposit`, 'Batch ACH Pull', Boolean(data?.ach?.canDeposit)),
      canWithdraw: getReadonlyListItemYesOrNo(`${prefix}ach.canWithdraw`, 'Batch ACH Push', Boolean(data?.ach?.canWithdraw)),
    },
    sameDayAch: {
      canDeposit: getReadonlyListItemYesOrNo(`${prefix}sameDayAch.canDeposit`, 'Batch Same-Day ACH Pull', Boolean(data?.sameDayAch?.canDeposit)),
      canWithdraw: getReadonlyListItemYesOrNo(`${prefix}sameDayAch.canWithdraw`, 'Batch Same-Day ACH Push', Boolean(data?.sameDayAch?.canWithdraw)),
    },
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingBatchControlsListItems(data: IxcFundingV1BatchFundingControls | undefined, prefix = '') {
  return {
    limits: getFundingBatchControlLimitsListItems(data?.limits, `${prefix}limits.`),
    settings: getFundingBatchControlSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingUsBankDetailListItems(data: IxcFundingV1UsBankDetails | undefined, prefix = '') {
  return {
    accountNumber: getReadonlyListItemString(`${prefix}accountNumber`, 'Account Number', data?.accountNumber),
    routingNumber: getReadonlyListItemString(`${prefix}routingNumber`, 'Routing Number', data?.routingNumber),
    accountHolderName: getReadonlyListItemString(`${prefix}accountHolderName`, 'Account Holder Name', data?.accountHolderName),
    accountType: getReadonlyListItemTranslatedString(`${prefix}accountType`, 'Account Type', {
      namespace: 'ixcFundingV1',
      enumName: 'IxcFundingV1BankAccountType',
      value: data?.accountType,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingDomesticWireSourceListItems(data: IxcFundingV1DomesticWireSource | undefined, prefix = '') {
  return {
    bankDetails: getFundingUsBankDetailListItems(data?.bankDetails, `${prefix}bankDetails`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingAccountNumberSourceListItems(data: IxcFundingV1AccountNumberSource | undefined, prefix = '') {
  return {
    enabled: getReadonlyListItemYesOrNo(`${prefix}enabled`, 'Enabled', getBooleanFromUndefinedOrNullable(data, 'enabled')),
    bankDetails: getFundingUsBankDetailListItems(data?.bankDetails, `${prefix}bankDetails`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingAchSourceListItems(data: IxcFundingV1AchSource | undefined, prefix = '') {
  return {
    bankDetails: getFundingUsBankDetailListItems(data?.bankDetails, `${prefix}bankDetails`),
    sameDayEnabled: getReadonlyListItemYesOrNo(`${prefix}sameDayEnabled`, 'Same-Day Enabled', Boolean(data?.sameDayEnabled)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingPlaidAccountDetailsAchListItems(data: IxcFundingV1PlaidAccountDetailsAch | undefined, prefix = '') {
  return {
    accountId: getReadonlyListItemExternalId(`${prefix}accountId`, 'Plaid Account ID', data?.accountId),
    accountNumber: getReadonlyListItemString(`${prefix}accountNumber`, 'Account Number', data?.accountNumber),
    routingNumber: getReadonlyListItemString(`${prefix}routingNumber`, 'Routing Number', data?.routingNumber),
    wireRoutingNumber: getReadonlyListItemString(`${prefix}wireRoutingNumber`, 'Wire Routing Number', data?.wireRoutingNumber),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingPlaidAccountDetailsListItems(data: IxcFundingV1PlaidAccountDetails | undefined, prefix = '') {
  return {
    accountId: getReadonlyListItemExternalId(`${prefix}accountId`, 'Plaid Account ID', data?.accountId),
    mask: getReadonlyListItemString(`${prefix}mask`, 'Mask', data?.mask),
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
    officialName: getReadonlyListItemString(`${prefix}officialName`, 'Official Name', data?.officialName),
    accountType: getReadonlyListItemString(`${prefix}accountType`, 'Account Type', data?.accountType),
    subtype: getReadonlyListItemString(`${prefix}subtype`, 'Account Subtype', data?.subtype),
    ach: getFundingPlaidAccountDetailsAchListItems(data?.ach, `${prefix}ach.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingPlaidProcessorTokenSourceListItems(data: IxcFundingV1PlaidProcessorTokenSource | undefined, prefix = '') {
  return {
    processorToken: getReadonlyListItemSensitiveString(
      `${prefix}processorToken`,
      'Processor Token',
      data?.processorToken,
      maskPlaidProcessorToken,
      data?.processorToken
        ? {
            columns: 1,
            align: 'end',
            valueTypography: { ...defaultValueStackedTypographyProps, wrap: 'anywhere', style: { width: '100%' } },
          }
        : undefined,
    ),
    nameOnAccount: getReadonlyListItemString(`${prefix}nameOnAccount`, 'Name on Account', data?.nameOnAccount),
    accountFromPlaid: getFundingPlaidAccountDetailsListItems(data?.accountFromPlaid, `${prefix}accountFromPlaid.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingSourceTypeListItems(data: IxcFundingV1SourceType | undefined, prefix = '') {
  if (!data) {
    return undefined;
  }

  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcFundingV1',
      oneOfName: 'IxcFundingV1SourceType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return undefined;
  }

  if ('achSource' in data) {
    return {
      '!type': 'achSource',
      value: {
        ...typeNameColumn,
        ...getFundingAchSourceListItems(data.achSource, `${prefix}achSource`),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  if ('domesticWireSource' in data) {
    return {
      '!type': 'domesticWireSource',
      value: {
        ...typeNameColumn,
        ...getFundingDomesticWireSourceListItems(data.domesticWireSource, `${prefix}domesticWireSource`),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  if ('accountNumberSource' in data) {
    return {
      '!type': 'accountNumberSource',
      value: {
        ...typeNameColumn,
        ...getFundingAccountNumberSourceListItems(data.accountNumberSource, `${prefix}accountNumberSource`),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  if ('plaidProcessorTokenSource' in data) {
    return {
      '!type': 'plaidProcessorTokenSource',
      value: {
        ...typeNameColumn,
        ...getFundingPlaidProcessorTokenSourceListItems(data.plaidProcessorTokenSource, `${prefix}plaidProcessorTokenSource`),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  exhaustiveCheck(data['!type']);

  return undefined;
}

export function getFundingSourceValidationListItems(data: IxcFundingV1SourceValidation | undefined, prefix = '') {
  if (!data) {
    return undefined;
  }

  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcFundingV1',
      oneOfName: 'IxcFundingV1SourceValidation',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if ('external' in data) {
    return {
      '!type': 'external',
      value: {
        ...typeNameColumn,
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  exhaustiveCheck(data['!type']);

  return undefined;
}

export function getFundingSourceCreatableSourceType(data: IxcFundingV1CreatableSourceType | undefined, prefix = '') {
  if (!data) {
    return undefined;
  }

  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcFundingV1',
      oneOfName: 'IxcFundingV1SourceType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return undefined;
  }

  if ('achSource' in data) {
    return {
      '!type': 'achSource',
      value: {
        ...typeNameColumn,
        ...getFundingAchSourceListItems(data.achSource, `${prefix}achSource`),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  if ('domesticWireSource' in data) {
    return {
      '!type': 'domesticWireSource',
      value: {
        ...typeNameColumn,
        ...getFundingDomesticWireSourceListItems(data.domesticWireSource, `${prefix}domesticWireSource`),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  if ('plaidProcessorTokenSource' in data) {
    return {
      '!type': 'plaidProcessorTokenSource',
      value: {
        ...typeNameColumn,
        ...getFundingPlaidProcessorTokenSourceListItems(data.plaidProcessorTokenSource, `${prefix}plaidProcessorTokenSource`),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  exhaustiveCheck(data['!type']);

  return undefined;
}

export function getFundingExternallyManagedListItems(data: IxcFundingV1ExternallyManaged | undefined, prefix = '') {
  if (!data) {
    return undefined;
  }

  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcFundingV1',
      oneOfName: 'IxcFundingV1SourceType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return undefined;
  }

  if ('plaidSource' in data) {
    return {
      '!type': 'plaidSource',
      value: {
        ...typeNameColumn,
        ...getFundingPlaidProcessorTokenSourceListItems(data.plaidSource, `${prefix}plaidProcessorTokenSource`),
      },
    } as const satisfies ReadonlyListItemOneOfRecord;
  }

  exhaustiveCheck(data['!type']);

  return undefined;
}

export function getFundingSourceSpecListItems(data: IxcFundingV1SourceSpec | undefined, prefix = '', sourceLink?: string) {
  return {
    isPrimary: getReadonlyListItemYesOrNo(`${prefix}isPrimary`, 'Is Primary', getBooleanFromUndefinedOrNullable(data, 'isPrimary')),
    nickname: getReadonlyListItemString(`${prefix}nickname`, 'Nickname', data?.nickname),
    sourceType: getFundingSourceTypeListItems(data?.sourceType, `${prefix}sourceType`),
    ownerType: getReadonlyListItemTranslatedString(`${prefix}ownerType`, 'Owner Type', {
      namespace: 'ixcFundingV1',
      enumName: 'IxcFundingV1BankAccountOwnerType',
      value: data?.ownerType,
    }),
    validation: getFundingSourceValidationListItems(data?.validation, `${prefix}validation`),
    compactSummary: getReadonlyListItemString(`${prefix}compactSummary`, 'Source', getCompactFundingSourceSummaryFromSpec(data), sourceLink),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingSourceDataListItems(data: IxcFundingV1SourceData | undefined, prefix = '') {
  return {
    spec: getFundingSourceSpecListItems(data?.spec, `${prefix}spec.`),
    accountNumberId: data?.accountNumberId
      ? getReadonlyListItemId(
          `${prefix}accountNumberId`,
          'Account Number ID',
          data.accountNumberId,
          getFundingAccountNumberDetailPath(data.accountNumberId),
        )
      : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingSourceStateListItems(
  data: IxcFundingV1SourceState | undefined,
  linkContext: ReplaceValuesAndMakeOptional<IxcFundingV1SourceKeys, string, boolean> = { accountId: true, programId: true },
) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    sourceId: getReadonlyListItemId(
      'sourceId',
      'Source ID',
      data?.sourceId,
      linkContext.sourceId ? getFundingSourceDetailPath(data?.sourceId, linkContext.accountId ? data?.accountId : undefined) : undefined,
    ),
    accountId: getReadonlyListItemId(
      'accountId',
      'Account ID',
      data?.accountId,
      linkContext.accountId ? getRegistrationAccountDetailPath(data?.accountId) : undefined,
    ),
    programId: getReadonlyListItemId(
      'programId',
      'Program ID',
      data?.programId,
      linkContext.programId ? getProgramDetailPath(data?.programId) : undefined,
    ),
    program: getReadonlyListItemTenant('program', 'Program', 'program', data?.programId),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcFundingV1',
      enumName: 'IxcFundingV1SourceStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <FundingSourceStatusPill status={data.status} /> : null),
    data: getFundingSourceDataListItems(data?.data, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingSourceDetailPageListItems(data: IxcFundingV1SourceState | undefined) {
  const items = getFundingSourceStateListItems(data);
  const typeNameLabel = 'Source Type';

  return {
    general: {
      left: [
        items.data.spec.sourceType?.value.typeName
          ? { ...items.data.spec.sourceType.value.typeName, title: typeNameLabel }
          : getReadonlyListItemString('sourceType', typeNameLabel, undefined),
        items.data.spec.nickname,
        ...(items.data.spec.sourceType?.value && items.data.spec.sourceType['!type'] === 'achSource'
          ? [
              items.data.spec.sourceType.value.bankDetails.routingNumber,
              items.data.spec.sourceType.value.bankDetails.accountNumber,
              { ...items.data.spec.sourceType.value.bankDetails.accountHolderName, title: 'Name on Account' },
              items.data.spec.sourceType.value.bankDetails.accountType,
              items.data.spec.sourceType.value.sameDayEnabled,
            ]
          : []),
        ...(items.data.spec.sourceType?.value && items.data.spec.sourceType['!type'] === 'accountNumberSource'
          ? [
              items.data.spec.sourceType.value.bankDetails.routingNumber,
              items.data.spec.sourceType.value.bankDetails.accountNumber,
              { ...items.data.spec.sourceType.value.bankDetails.accountHolderName, title: 'Name on Account' },
              items.data.spec.sourceType.value.bankDetails.accountType,
              items.data.spec.sourceType.value.enabled,
            ]
          : []),
        ...(items.data.spec.sourceType?.value && items.data.spec.sourceType['!type'] === 'domesticWireSource'
          ? [
              items.data.spec.sourceType.value.bankDetails.routingNumber,
              items.data.spec.sourceType.value.bankDetails.accountNumber,
              { ...items.data.spec.sourceType.value.bankDetails.accountHolderName, title: 'Name on Account' },
              items.data.spec.sourceType.value.bankDetails.accountType,
            ]
          : []),
        ...(items.data.spec.sourceType?.value && items.data.spec.sourceType['!type'] === 'plaidProcessorTokenSource'
          ? [
              items.data.spec.sourceType.value.processorToken,
              items.data.spec.sourceType.value.accountFromPlaid.accountId,
              items.data.spec.sourceType.value.accountFromPlaid.name,
              items.data.spec.sourceType.value.accountFromPlaid.mask,
              items.data.spec.sourceType.value.accountFromPlaid.officialName,
              items.data.spec.sourceType.value.accountFromPlaid.accountType,
              items.data.spec.sourceType.value.accountFromPlaid.subtype,
              items.data.spec.sourceType.value.accountFromPlaid.ach.accountNumber,
              items.data.spec.sourceType.value.accountFromPlaid.ach.routingNumber,
              items.data.spec.sourceType.value.accountFromPlaid.ach.wireRoutingNumber,
            ]
          : []),
        { ...items.data.spec.isPrimary, title: 'Set as Primary' },
      ] as const satisfies ReadonlyListItem[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.sourceId,
        items.accountId,
        items.programId,
        ...(items.data.accountNumberId ? [items.data.accountNumberId] : []),
      ] as const satisfies ReadonlyListItem[],
    },
  };
}

function SourceCompactSummaryNode({ sourceId }: { sourceId: string | undefined }) {
  const { isPending, data } = useIxcFundingV1SourceQueryServiceSourceGet(sourceId ? { sourceId } : undefined);

  if (isPending) {
    return null;
  }

  if (!data) {
    return getShortenedId(sourceId);
  }

  return getCompactFundingSourceSummaryFromSpec(data.source.data.spec);
}

export function getFundingSourceCompactSummaryListItem(id: string, label: ReactNode, sourceId: string | undefined) {
  return getReadonlyListItemNode(id, label, sourceId ? <SourceCompactSummaryNode sourceId={sourceId} /> : undefined);
}
